import type { TicketListItem } from '../api/types';
import { versionNames } from './format';

/**
 * The CONTENT of a ticket row's second line, as data rather than as JSX.
 *
 * 🔴 WHY THIS IS A MODULE AND NOT JUST MARKUP. The second line is how 26 tickets get scanned without
 *   opening one, and the rule that keeps it scannable — the sprint is a SEPARATE PART, never a
 *   fourth item appended to the dot-separated text — is a claim about content, not about styling.
 *   Expressed as a function it can be asserted; expressed only as JSX it can only be eyeballed.
 *   ⭐ `sprint` and `text` coming back as two fields IS the no-wrap mechanism at the data level: the
 *     renderer gives the sprint a `shrink-0` chip and lets `text` truncate beside it, which it could
 *     not do if the sprint had already been concatenated into `text`.
 */
export interface SecondLineParts {
  /** The sprint chip, or null when the ticket has no sprint (or the API did not send one). */
  sprint: string | null;
  /** `Significant / Large • v1.0.33, v1.0.34 • AI, MCP` — the part allowed to truncate. */
  text: string;
}

/**
 * ⚠️ A segment appears only when the API actually SENT the field. `undefined` (not returned) and
 * `[]` (returned, empty) are different facts and are NOT flattened into one another — an endpoint
 * that forgot `modules` must not be indistinguishable from 50 tickets that genuinely have none.
 *
 * ⚠️ The sprint is the ONE place that distinction is deliberately not drawn: absent and null both
 * yield `null`, because the row renders nothing in either case and a reader could act on neither.
 */
export function secondLineParts(
  /**
   * ⚠️ `Partial`, and that is the point rather than laziness: the function's whole job is to tell
   * a field that is ABSENT from one that is present-and-empty, so every field must be allowed to be
   * absent. A required `Pick` would make "the API did not send modules" unrepresentable in the type
   * — the exact case the body below branches on.
   */
  ticket: Partial<Pick<TicketListItem, 'impact' | 'modules' | 'fix_versions' | 'sprint_name'>>,
  impactLabel: string | null,
): SecondLineParts {
  const segments: string[] = [];

  if (ticket.impact !== undefined) segments.push(impactLabel ?? 'None');

  if (ticket.fix_versions !== undefined) {
    // ⚠️ Two shapes in the wild — see `versionNames`. The API sends plain names; this app was built
    // against a `{id, name}` guess and would otherwise print `undefined` for every version.
    const names = versionNames(ticket.fix_versions);
    segments.push(names.length > 0 ? names.join(', ') : 'None');
  }

  if (ticket.modules !== undefined) {
    segments.push(ticket.modules.length > 0 ? ticket.modules.join(', ') : 'None');
  }

  return {
    /**
     * ⚠️ `||`, NOT `??`. `??` passes an EMPTY STRING through, and the renderer treats `''` as falsy
     * and draws no chip — so the function would be returning `''` while the row showed nothing, a
     * contract that disagrees with its only caller. `||` normalises absent, null and empty to the
     * one value that means "no chip". Caught by a test, not by reading it.
     */
    sprint: ticket.sprint_name || null,
    text: segments.join(' • '),
  };
}
